import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { map, of, pipe, switchMap, tap, timer } from 'rxjs';
import {
  isSafeImageSource,
  richHtmlToPlainText,
  sanitizeRichHtml,
} from '@maximizer/shared/util';
import { schema } from '@progress/kendo-angular-editor';
import { getHtml, parseContent } from '@progress/kendo-editor-common';
import {
  EmailDraft,
  EmailRecipient,
  EmailRecipientField,
  OutgoingEmailMessage,
} from '../models/email-draft';
import {
  dedupeRecipients,
  emailKey,
  emptyEmailDraft,
  normalizeDraftPatch,
  sameRecipients,
} from '../utils/email-draft.util';
import { EmailRecipientSearchService } from '../services/email-recipient-search.service';
import { EmailSignatureService } from '../services/email-signature.service';

export interface EmailComposeState {
  draft: EmailDraft;
  baseline: EmailDraft | null;
  searchResults: EmailRecipient[];
  signatureHtml: string;
  signatureLoading: boolean;
}

const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_MIN_LENGTH = 2;
const SIGNATURE_SEPARATOR = '<p></p>';
const EMPTY_EDITOR_HTML = '<p></p>';

const initialState = (): EmailComposeState => ({
  draft: emptyEmailDraft(),
  baseline: null,
  searchResults: [],
  signatureHtml: '',
  signatureLoading: false,
});

export const EmailComposeStore = signalStore(
  withState<EmailComposeState>(initialState),
  withComputed(({ draft, baseline, searchResults, signatureHtml }) => {
    const sanitizedBodyHtml = computed(() =>
      sanitizeRichHtml(draft.bodyHtml()),
    );
    const plainBody = computed(() => richHtmlToPlainText(sanitizedBodyHtml()));
    const senderEmail = computed(() => (draft.from()?.email ?? '').trim());
    const signatureText = computed(() =>
      visibleText(sanitizeRichHtml(signatureHtml())),
    );
    const signatureImages = computed(() => safeImageSources(signatureHtml()));
    return {
      sanitizedBodyHtml,
      plainBody,
      senderEmail,
      hasSender: computed(() => senderEmail() !== ''),
      hasBody: computed(() => {
        const text = visibleText(sanitizedBodyHtml());
        if (text !== '' && text !== signatureText()) {
          return true;
        }
        return safeImageSources(sanitizedBodyHtml()).some(
          (src) => !signatureImages().includes(src),
        );
      }),
      hasAnyRecipient: computed(
        () => draft.to().length + draft.cc().length + draft.bcc().length > 0,
      ),
      outgoingMessage: computed<OutgoingEmailMessage>(() => ({
        from: senderEmail(),
        to: draft.to().map((recipient) => recipient.email.trim()),
        cc: draft.cc().map((recipient) => recipient.email.trim()),
        bcc: draft.bcc().map((recipient) => recipient.email.trim()),
        subject: draft.subject().trim(),
        bodyHtml: sanitizedBodyHtml(),
      })),
      dirty: computed(() => {
        const base = baseline();
        if (base === null) {
          return false;
        }
        return (
          draft.subject() !== base.subject ||
          draft.bodyHtml() !== base.bodyHtml ||
          !sameRecipients(draft.to(), base.to) ||
          !sameRecipients(draft.cc(), base.cc) ||
          !sameRecipients(draft.bcc(), base.bcc)
        );
      }),
      namesByEmail: computed(() => {
        const names = new Map<string, string>();
        for (const recipient of searchResults()) {
          const name = (recipient.name ?? '').trim();
          if (name !== '') {
            names.set(emailKey(recipient.email), name);
          }
        }
        return names;
      }),
    };
  }),
  withMethods((store, searchService = inject(EmailRecipientSearchService)) => ({
    search: rxMethod<string>(
      pipe(
        map((term) => term.trim()),
        switchMap((term) =>
          term.length < SEARCH_MIN_LENGTH
            ? of([])
            : timer(SEARCH_DEBOUNCE_MS).pipe(
                switchMap(() => searchService.search(term)),
              ),
        ),
        tap((searchResults) => patchState(store, { searchResults })),
      ),
    ),
  })),
  withMethods((store, signatureService = inject(EmailSignatureService)) => ({
    loadSignature: rxMethod<string>(
      pipe(
        tap(() => patchState(store, { signatureLoading: true })),
        switchMap((userKey) => signatureService.read(userKey)),
        map((rawSignature) => editorHtml(sanitizeRichHtml(rawSignature))),
        tap((signatureHtml) => {
          const previous = store.signatureHtml();
          const baseline = store.baseline();
          patchState(store, {
            signatureHtml,
            signatureLoading: false,
            draft: {
              ...store.draft(),
              bodyHtml: resign(store.draft.bodyHtml(), previous, signatureHtml),
            },
            baseline:
              baseline === null
                ? null
                : {
                    ...baseline,
                    bodyHtml: resign(
                      baseline.bodyHtml,
                      previous,
                      signatureHtml,
                    ),
                  },
          });
        }),
      ),
    ),
    load(patch: Partial<EmailDraft>): void {
      const draft = { ...store.draft(), ...normalizeDraftPatch(patch) };
      patchState(store, { draft, baseline: draft });
    },
    update(patch: Partial<EmailDraft>): void {
      patchState(store, {
        draft: { ...store.draft(), ...normalizeDraftPatch(patch) },
      });
    },
    setRecipients(field: EmailRecipientField, next: EmailRecipient[]): void {
      patchState(store, {
        draft: { ...store.draft(), [field]: dedupeRecipients(next) },
      });
    },
    clear(): void {
      store.search('');
      patchState(store, initialState());
    },
    reset(): void {
      const baseline = store.baseline();
      if (baseline === null) {
        return;
      }
      patchState(store, {
        draft: { ...baseline, from: store.draft.from() },
        searchResults: [],
      });
    },
  })),
);

export type EmailComposeStore = InstanceType<typeof EmailComposeStore>;

function withSignature(bodyHtml: string, signatureHtml: string): string {
  return signatureHtml === ''
    ? bodyHtml
    : `${bodyHtml}${SIGNATURE_SEPARATOR}${signatureHtml}`;
}

function resign(bodyHtml: string, previous: string, next: string): string {
  if (previous === '') {
    return withSignature(bodyHtml, next);
  }
  if (!bodyHtml.endsWith(previous)) {
    return bodyHtml;
  }
  const unsigned = bodyHtml.slice(0, -previous.length);
  if (next !== '') {
    return `${unsigned}${next}`;
  }
  return unsigned.endsWith(SIGNATURE_SEPARATOR)
    ? unsigned.slice(0, -SIGNATURE_SEPARATOR.length)
    : unsigned;
}

function editorHtml(html: string): string {
  const serialized = getHtml({
    doc: parseContent(html.trim(), schema, { preserveWhitespace: false }),
  });
  return serialized === EMPTY_EDITOR_HTML ? '' : serialized;
}

function visibleText(html: string): string {
  return richHtmlToPlainText(html).replace(/\s+/g, '');
}

function safeImageSources(html: string): string[] {
  const images = new DOMParser().parseFromString(html, 'text/html').images;
  return Array.from(images, (image) => image.getAttribute('src') ?? '').filter(
    isSafeImageSource,
  );
}
